import React, { useState, useEffect } from 'react';

const LANG_OPTIONS = [
  { value: 'tr', label: 'Türkçe',   flag: '🇹🇷' },
  { value: 'en', label: 'English',  flag: '🇬🇧' },
  { value: 'de', label: 'Deutsch',  flag: '🇩🇪' },
  { value: 'fr', label: 'Français', flag: '🇫🇷' },
  { value: 'ar', label: 'العربية',  flag: '🇸🇦' },
];

const DEFAULT_FORM = {
  name: '', type: 'voice', language: 'tr', language_label: 'Türkçe',
  working_hours: '7/24', status: 'active',
  elevenlabs_agent_id: '', elevenlabs_phone_number_id: ''
};

// ElevenLabs Server Tools (Webhook Tools) olarak agent'a eklenecek canlı araçlar -
// server/routes/webhook.js'teki /elevenlabs-tools/:branchId/* route'larına karşılık
// gelir (bkz. server/utils/elevenLabsTools.js). Kullanıcı bunları ElevenLabs
// dashboard'unda ELLE tanımlıyor - biz sadece kopyalanacak URL/schema'yı gösteriyoruz.
const TOOL_DEFS = [
  {
    name: 'check_availability', path: 'check-availability', method: 'POST',
    description: 'Belirtilen tarihte bir hizmet + personel için müsait saatleri döner.',
    params: {
      service_name: 'string - Müşterinin istediği hizmetin adı (LLM Prompt)',
      staff_name: 'string - Randevu alınacak personelin adı (LLM Prompt)',
      date: 'string - YYYY-MM-DD formatında tarih (LLM Prompt)'
    }
  },
  {
    name: 'create_appointment', path: 'create-appointment', method: 'POST',
    description: 'Belirtilen tarih/saat için randevu oluşturur (durum: onay bekliyor).',
    params: {
      service_name: 'string - Hizmet adı (LLM Prompt)',
      staff_name: 'string - Personel adı (LLM Prompt)',
      date: 'string - YYYY-MM-DD (LLM Prompt)',
      time: 'string - HH:MM (LLM Prompt)',
      caller_name: 'string - Arayan kişinin adı (LLM Prompt)',
      notes: 'string - Ek notlar, opsiyonel (LLM Prompt)',
      caller_phone: 'string - Arayanın telefonu (Dynamic Variable: {{system__caller_id}} gelen / {{phone}} giden aramada — LLM Prompt OLARAK BIRAKMAYIN)'
    }
  },
  {
    name: 'create_pre_consultation', path: 'create-pre-consultation', method: 'POST',
    description: 'Randevu yerine önce bir ön görüşme talebi kaydı oluşturur.',
    params: {
      caller_name: 'string - Arayan kişinin adı (LLM Prompt)',
      sector: 'string - İlgilendiği konu/sektör, opsiyonel (LLM Prompt)',
      preferred_date: 'string - Tercih ettiği tarih, opsiyonel (LLM Prompt)',
      notes: 'string - Ek notlar, opsiyonel (LLM Prompt)',
      caller_phone: 'string - Arayanın telefonu (Dynamic Variable: {{system__caller_id}} gelen / {{phone}} giden aramada — LLM Prompt OLARAK BIRAKMAYIN)'
    }
  },
  {
    name: 'get_services', path: 'get-services', method: 'POST',
    description: 'Hizmet adı eşleşmediğinde kullanılan yardımcı: şubenin tüm hizmet adlarını döner.',
    params: {}
  },
  {
    name: 'get_staff', path: 'get-staff', method: 'POST',
    description: 'Personel adı eşleşmediğinde kullanılan yardımcı: şubedeki tüm personel adlarını döner.',
    params: {}
  }
];

// apiBase/token: yonetim panelindeki AI Center bu bilesenleri DEGISTIRMEDEN
// yeniden kullanabilsin diye opsiyonel - verilmezse tenant'taki mevcut
// davranis (kendi crm_token'i + /api/callops) AYNEN korunur.
const AgentsTab = ({ apiBase = '/api/callops', token: tokenProp } = {}) => {
  const [agents, setAgents]       = useState([]);
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editAgent, setEditAgent] = useState(null);
  const [form, setForm]           = useState(DEFAULT_FORM);
  const [error, setError]         = useState('');
  const [activeType, setActiveType] = useState('all');
  const [copiedTool, setCopiedTool] = useState(null);

  const copyToolUrl = (toolPath) => {
    const url = `${window.location.origin}/webhook/elevenlabs-tools/${editAgent.branch_id}/${toolPath}`;
    navigator.clipboard.writeText(url);
    setCopiedTool(toolPath);
    setTimeout(() => setCopiedTool(null), 2000);
  };

  const token = tokenProp || localStorage.getItem('crm_token');
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  useEffect(() => { fetchAgents(); }, []);

  const fetchAgents = async () => {
    setLoading(true);
    try {
      const res  = await fetch(`${apiBase}/agents`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setAgents(data.agents || []);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const openCreate = () => { setEditAgent(null); setForm(DEFAULT_FORM); setError(''); setShowModal(true); };

  const openEdit = (agent) => {
    setEditAgent(agent);
    let cfg = {};
    try { cfg = typeof agent.config === 'string' ? JSON.parse(agent.config || '{}') : (agent.config || {}); } catch { cfg = {}; }
    setForm({
      name: agent.name || '', type: agent.type || 'voice', language: agent.language || 'tr',
      language_label: agent.language_label || 'Türkçe', working_hours: agent.working_hours || '7/24',
      status: agent.status || 'active',
      elevenlabs_agent_id: cfg.elevenlabs_agent_id || '',
      elevenlabs_phone_number_id: cfg.elevenlabs_phone_number_id || '',
    });
    setError(''); setShowModal(true);
  };

  const closeModal = () => { setShowModal(false); setEditAgent(null); setForm(DEFAULT_FORM); setError(''); };

  const handleSave = async () => {
    if (!form.name.trim()) { setError('Agent adı zorunlu'); return; }
    setSaving(true); setError('');
    try {
      const url    = editAgent ? `${apiBase}/agents/${editAgent.id}` : `${apiBase}/agents`;
      const method = editAgent ? 'PUT' : 'POST';
      const { elevenlabs_agent_id, elevenlabs_phone_number_id, ...rest } = form;
      const body = { ...rest, config: { elevenlabs_agent_id: elevenlabs_agent_id || '', elevenlabs_phone_number_id: elevenlabs_phone_number_id || '' } };
      const res  = await fetch(url, { method, headers, body: JSON.stringify(body) });
      const data = await res.json();
      if (!res.ok || data.error) { setError(data.error || 'Kayıt hatası'); setSaving(false); return; }
      closeModal(); fetchAgents();
    } catch (err) { setError('Sunucu hatası'); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Agent silinsin mi?')) return;
    try {
      await fetch(`${apiBase}/agents/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      fetchAgents();
    } catch (err) { console.error(err); }
  };

  const handleToggleStatus = async (agent) => {
    const newStatus = agent.status === 'active' ? 'disabled' : 'active';
    try {
      await fetch(`${apiBase}/agents/${agent.id}`, { method: 'PUT', headers, body: JSON.stringify({ ...agent, status: newStatus }) });
      fetchAgents();
    } catch (err) { console.error(err); }
  };

  const langFlag = (code) => LANG_OPTIONS.find(l => l.value === code)?.flag || '🌐';
  const formatDur = (s) => { if (!s) return '0:00'; return `${Math.floor(s/60)}:${String(s%60).padStart(2,'0')}`; };

  const filtered = activeType === 'all' ? agents : agents.filter(a => a.type === activeType);
  const voiceCount = agents.filter(a => a.type === 'voice').length;
  const chatCount  = agents.filter(a => a.type === 'chat').length;
  const activeCount = agents.filter(a => a.status === 'active').length;

  const AgentCard = ({ agent }) => {
    const isActive = agent.status === 'active';
    return (
      <div className="agent-card">
        <div className="agent-card-top">
          <div className="agent-avatar" style={{ background: agent.type === 'voice' ? 'linear-gradient(135deg,#7c3aed,#a78bfa)' : 'linear-gradient(135deg,#059669,#34D399)' }}>
            {agent.type === 'voice' ? '🎙️' : '💬'}
          </div>
          <div className="agent-info">
            <div className="agent-name">{agent.name}</div>
            <div className="agent-meta">{langFlag(agent.language)} {agent.language_label} · {agent.working_hours}</div>
          </div>
          <div className={`agent-status-dot ${agent.status}`} title={agent.status} />
        </div>

        <div className="agent-status-badge-row">
          <span className={`agent-status-badge ${agent.status}`}>
            {agent.status === 'active' ? '● Aktif' : agent.status === 'waiting' ? '◌ Bekliyor' : '○ Pasif'}
          </span>
          <span className="agent-type-badge">{agent.type === 'voice' ? '🎙️ Voice' : '💬 Chat'}</span>
        </div>

        <div className="agent-stats">
          <div className="stat-box"><span className="stat-v">{agent.total_calls || 0}</span><span className="stat-l">Toplam</span></div>
          <div className="stat-box"><span className="stat-v">{agent.today_calls || 0}</span><span className="stat-l">Bugün</span></div>
          {agent.type === 'voice' && <div className="stat-box"><span className="stat-v">{formatDur(agent.avg_duration_seconds)}</span><span className="stat-l">Ort.Süre</span></div>}
          <div className="stat-box"><span className="stat-v" style={{ color: '#34D399' }}>{agent.success_rate || 0}%</span><span className="stat-l">Başarı</span></div>
        </div>

        {agent.type === 'voice' && (
          <div className="load-bar-row">
            <span className="load-lbl">Yük</span>
            <div className="load-track">
              <div className="load-fill" style={{
                width: `${agent.current_load || 0}%`,
                background: (agent.current_load || 0) > 80 ? '#F87171' : (agent.current_load || 0) > 50 ? '#FBBF24' : '#34D399'
              }} />
            </div>
            <span className="load-pct">{agent.current_load || 0}%</span>
          </div>
        )}

        {Array.isArray(agent.sectors) && agent.sectors.length > 0 && (
          <div className="agent-sectors">
            {agent.sectors.map((s, i) => <span key={i} className="sector-tag">{s}</span>)}
          </div>
        )}

        <div className="agent-actions">
          <button className="act-btn edit" onClick={() => openEdit(agent)}>✏️ Düzenle</button>
          <button className="act-btn toggle" onClick={() => handleToggleStatus(agent)}>
            {isActive ? '⏸ Durdur' : '▶ Aktifleştir'}
          </button>
          <button className="act-btn del" onClick={() => handleDelete(agent.id)}>🗑</button>
        </div>
      </div>
    );
  };

  return (
    <div className="agents-tab">
      {/* Header */}
      <div className="agents-header">
        <div>
          <h2>🤖 AI Agentlar</h2>
          <p style={{ margin:0, color:'#6B7280', fontSize:13 }}>{agents.length} agent tanımlı · {activeCount} aktif</p>
        </div>
        <button className="btn-new-agent" onClick={openCreate}>+ Yeni Agent</button>
      </div>

      {/* Summary */}
      <div className="agents-summary">
        <div className="summary-card" style={{ borderColor: 'rgba(167,139,250,0.3)' }}>
          <span style={{ fontSize:24 }}>🎙️</span>
          <div><div className="sum-val">{voiceCount}</div><div className="sum-lbl">Voice Agent</div></div>
        </div>
        <div className="summary-card" style={{ borderColor: 'rgba(52,211,153,0.3)' }}>
          <span style={{ fontSize:24 }}>💬</span>
          <div><div className="sum-val">{chatCount}</div><div className="sum-lbl">Chat Agent</div></div>
        </div>
        <div className="summary-card" style={{ borderColor: 'rgba(96,165,250,0.3)' }}>
          <span style={{ fontSize:24 }}>✅</span>
          <div><div className="sum-val">{activeCount}</div><div className="sum-lbl">Aktif</div></div>
        </div>
      </div>

      {/* Filter */}
      <div className="type-filter">
        {[['all','Tümü'],['voice','🎙️ Voice'],['chat','💬 Chat']].map(([t,l]) => (
          <button key={t} className={activeType === t ? 'active' : ''} onClick={() => setActiveType(t)}>{l}</button>
        ))}
      </div>

      {loading ? (
        <div className="agents-loading"><div className="ag-spinner" /><span>Yükleniyor...</span></div>
      ) : filtered.length === 0 ? (
        <div className="empty-agents">
          <div style={{ fontSize:56, marginBottom:16 }}>🤖</div>
          <h3>Henüz AI Agent yok</h3>
          <p>İlk agent'ı oluşturun ve AI'ı çalışmaya başlatın</p>
          <button className="btn-new-agent" onClick={openCreate}>+ İlk Agent'ı Ekle</button>
        </div>
      ) : (
        <div className="agent-grid">
          {filtered.map(a => <AgentCard key={a.id} agent={a} />)}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <div className="modal-hdr">
              <h2>{editAgent ? '✏️ Agent Düzenle' : '🤖 Yeni Agent Ekle'}</h2>
              <button className="modal-x" onClick={closeModal}>×</button>
            </div>
            <div className="modal-bdy">
              {error && <div className="form-error">⚠️ {error}</div>}

              <div className="fgrp">
                <label>Agent Adı *</label>
                <input className="finput" placeholder="Örn: TR-01 Voice" value={form.name}
                  onChange={e => setForm({ ...form, name: e.target.value })} autoFocus />
              </div>

              <div className="frow">
                <div className="fgrp">
                  <label>Tür</label>
                  <select className="fselect" value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}>
                    <option value="voice">🎙️ Voice</option>
                    <option value="chat">💬 Chat</option>
                  </select>
                </div>
                <div className="fgrp">
                  <label>Durum</label>
                  <select className="fselect" value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
                    <option value="active">✅ Aktif</option>
                    <option value="waiting">⏳ Bekliyor</option>
                    <option value="disabled">❌ Pasif</option>
                  </select>
                </div>
              </div>

              <div className="frow">
                <div className="fgrp">
                  <label>Dil</label>
                  <select className="fselect" value={form.language} onChange={e => {
                    const opt = LANG_OPTIONS.find(l => l.value === e.target.value);
                    setForm({ ...form, language: e.target.value, language_label: opt?.label || '' });
                  }}>
                    {LANG_OPTIONS.map(l => <option key={l.value} value={l.value}>{l.flag} {l.label}</option>)}
                  </select>
                </div>
                <div className="fgrp">
                  <label>Çalışma Saatleri</label>
                  <input className="finput" placeholder="7/24" value={form.working_hours}
                    onChange={e => setForm({ ...form, working_hours: e.target.value })} />
                </div>
              </div>

              {form.type === 'voice' && (
                <div className="elevenlabs-section">
                  <div className="section-label">🔊 ElevenLabs Konfigürasyonu</div>
                  <div className="frow">
                    <div className="fgrp">
                      <label>Agent ID</label>
                      <input className="finput" placeholder="agent_xxxxx" value={form.elevenlabs_agent_id}
                        onChange={e => setForm({ ...form, elevenlabs_agent_id: e.target.value })} />
                    </div>
                    <div className="fgrp">
                      <label>Phone Number ID</label>
                      <input className="finput" placeholder="phnum_xxxxx" value={form.elevenlabs_phone_number_id}
                        onChange={e => setForm({ ...form, elevenlabs_phone_number_id: e.target.value })} />
                    </div>
                  </div>

                  {editAgent && (
                    <div className="tools-section">
                      <div className="section-label">🛠️ Sunucu Araçları (Tools)</div>
                      <p className="tools-hint">
                        Bu araçları ElevenLabs dashboard'unda agent'ınıza "Server Tool / Webhook Tool"
                        olarak elle ekleyin. Her tool için: isim, açıklama, HTTP metodu, URL ve
                        parametreler aşağıda. <code>caller_phone</code> parametresini ElevenLabs'ta
                        mutlaka <strong>"Dynamic Variable"</strong> değer tipine bağlayın
                        (<code>{'{{system__caller_id}}'}</code> gelen arama, <code>{'{{phone}}'}</code> giden
                        arama için) — <strong>"LLM Prompt" olarak BIRAKMAYIN.</strong> Ayrıca her tool'un
                        "Custom Headers" alanına Ayarlar sayfasında ürettiğiniz Sunucu Araçları Secret'ını
                        <code>X-Ritsa-Tools-Secret</code> header'ı olarak ekleyin.
                      </p>
                      {TOOL_DEFS.map(tool => (
                        <div key={tool.path} className="tool-card">
                          <div className="tool-card-hdr">
                            <span className="tool-name">{tool.name}</span>
                            <button type="button" className="tool-copy-btn" onClick={() => copyToolUrl(tool.path)}>
                              {copiedTool === tool.path ? '✅ Kopyalandı' : '📋 URL Kopyala'}
                            </button>
                          </div>
                          <div className="tool-desc">{tool.description}</div>
                          <div className="tool-url">
                            {tool.method} {window.location.origin}/webhook/elevenlabs-tools/{editAgent.branch_id}/{tool.path}
                          </div>
                          {Object.keys(tool.params).length > 0 && (
                            <pre className="tool-params">{JSON.stringify(tool.params, null, 2)}</pre>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="modal-ftr">
              <button className="btn-cancel" onClick={closeModal}>İptal</button>
              <button className="btn-save" onClick={handleSave} disabled={saving}>
                {saving ? '⏳ Kaydediliyor...' : '✅ Kaydet'}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .agents-tab { display:flex; flex-direction:column; gap:24px; }

        .agents-header { display:flex; justify-content:space-between; align-items:flex-start; }
        .agents-header h2 { margin:0 0 2px; font-size:22px; font-weight:700; color:#F9FAFB; }

        .btn-new-agent { padding:11px 22px; background:linear-gradient(135deg,#7c3aed,#a78bfa); color:#fff; border:none; border-radius:12px; font-size:14px; font-weight:600; cursor:pointer; transition:all 0.2s; }
        .btn-new-agent:hover { transform:translateY(-1px); box-shadow:0 6px 20px rgba(124,58,237,0.4); }

        .agents-summary { display:grid; grid-template-columns:repeat(3,1fr); gap:12px; }
        .summary-card { background:rgba(17,17,24,0.7); border:1px solid; border-radius:14px; padding:16px 20px; display:flex; align-items:center; gap:16px; backdrop-filter:blur(16px); }
        .sum-val { font-size:26px; font-weight:800; color:#F9FAFB; line-height:1; }
        .sum-lbl { font-size:12px; color:#6B7280; text-transform:uppercase; letter-spacing:0.04em; margin-top:2px; }

        .type-filter { display:flex; gap:4px; background:rgba(255,255,255,0.04); border:1px solid rgba(255,255,255,0.07); padding:4px; border-radius:12px; width:fit-content; }
        .type-filter button { padding:8px 16px; border:none; background:transparent; color:#6B7280; font-size:13px; font-weight:500; cursor:pointer; border-radius:8px; transition:all 0.2s; }
        .type-filter button.active { background:linear-gradient(135deg,#7c3aed,#a78bfa); color:#fff; }

        .agents-loading { display:flex; align-items:center; justify-content:center; gap:16px; padding:80px; color:#6B7280; font-size:14px; flex-direction:column; }
        .ag-spinner { width:36px; height:36px; border:3px solid rgba(255,255,255,0.1); border-top-color:#a78bfa; border-radius:50%; animation:spin 0.8s linear infinite; }
        @keyframes spin { to { transform:rotate(360deg); } }

        .empty-agents { text-align:center; padding:80px 20px; color:#9CA3AF; }
        .empty-agents h3 { color:#E5E7EB; font-size:20px; margin:0 0 8px; }
        .empty-agents p { margin:0 0 24px; }

        .agent-grid { display:grid; grid-template-columns:repeat(auto-fill, minmax(300px,1fr)); gap:16px; }

        .agent-card { background:rgba(17,17,24,0.7); border:1px solid rgba(255,255,255,0.07); border-radius:18px; padding:20px; transition:all 0.25s; display:flex; flex-direction:column; gap:14px; backdrop-filter:blur(16px); }
        .agent-card:hover { border-color:rgba(167,139,250,0.25); transform:translateY(-2px); box-shadow:0 12px 32px rgba(0,0,0,0.3); }

        .agent-card-top { display:flex; align-items:center; gap:12px; }
        .agent-avatar { width:46px; height:46px; border-radius:13px; display:flex; align-items:center; justify-content:center; font-size:22px; flex-shrink:0; }
        .agent-info { flex:1; }
        .agent-name { font-weight:700; font-size:15px; color:#F9FAFB; }
        .agent-meta { font-size:12px; color:#6B7280; margin-top:2px; }
        .agent-status-dot { width:10px; height:10px; border-radius:50%; flex-shrink:0; }
        .agent-status-dot.active { background:#34D399; box-shadow:0 0 8px rgba(52,211,153,0.6); }
        .agent-status-dot.waiting { background:#FBBF24; }
        .agent-status-dot.disabled { background:#6B7280; }

        .agent-status-badge-row { display:flex; gap:8px; flex-wrap:wrap; }
        .agent-status-badge { padding:3px 10px; border-radius:20px; font-size:11px; font-weight:700; }
        .agent-status-badge.active { background:rgba(52,211,153,0.12); color:#34D399; border:1px solid rgba(52,211,153,0.2); }
        .agent-status-badge.waiting { background:rgba(251,191,36,0.12); color:#FBBF24; border:1px solid rgba(251,191,36,0.2); }
        .agent-status-badge.disabled { background:rgba(107,114,128,0.12); color:#9CA3AF; border:1px solid rgba(107,114,128,0.2); }
        .agent-type-badge { padding:3px 10px; border-radius:20px; font-size:11px; font-weight:600; background:rgba(167,139,250,0.1); color:#a78bfa; border:1px solid rgba(167,139,250,0.2); }

        .agent-stats { display:flex; gap:8px; }
        .stat-box { flex:1; text-align:center; padding:10px 4px; background:rgba(255,255,255,0.04); border-radius:10px; }
        .stat-v { display:block; font-size:15px; font-weight:700; color:#E5E7EB; }
        .stat-l { display:block; font-size:10px; color:#6B7280; margin-top:2px; }

        .load-bar-row { display:flex; align-items:center; gap:10px; }
        .load-lbl { font-size:11px; color:#6B7280; width:28px; }
        .load-track { flex:1; height:5px; background:rgba(255,255,255,0.07); border-radius:3px; overflow:hidden; }
        .load-fill { height:100%; border-radius:3px; transition:width 0.4s; }
        .load-pct { font-size:11px; font-weight:600; color:#9CA3AF; width:28px; text-align:right; }

        .agent-sectors { display:flex; flex-wrap:wrap; gap:6px; }
        .sector-tag { padding:3px 10px; background:rgba(167,139,250,0.1); color:#a78bfa; border-radius:20px; font-size:11px; border:1px solid rgba(167,139,250,0.15); }

        .agent-actions { display:flex; gap:6px; border-top:1px solid rgba(255,255,255,0.05); padding-top:12px; }
        .act-btn { flex:1; padding:8px 4px; border:none; border-radius:9px; font-size:12px; font-weight:600; cursor:pointer; transition:all 0.15s; }
        .act-btn.edit { background:rgba(167,139,250,0.12); color:#a78bfa; border:1px solid rgba(167,139,250,0.15); }
        .act-btn.toggle { background:rgba(251,191,36,0.12); color:#FBBF24; border:1px solid rgba(251,191,36,0.15); }
        .act-btn.del { background:rgba(248,113,113,0.1); color:#F87171; flex:0; padding:8px 12px; border:1px solid rgba(248,113,113,0.15); }
        .act-btn:hover { opacity:0.8; transform:translateY(-1px); }

        /* Modal */
        .modal-overlay { position:fixed; inset:0; background:rgba(0,0,0,0.7); backdrop-filter:blur(8px); display:flex; align-items:center; justify-content:center; z-index:1000; padding:20px; }
        .modal-box { background:#0f0f16; border:1px solid rgba(255,255,255,0.1); border-radius:20px; width:100%; max-width:500px; overflow:hidden; box-shadow:0 24px 60px rgba(0,0,0,0.6); }
        .modal-hdr { display:flex; justify-content:space-between; align-items:center; padding:20px 24px; border-bottom:1px solid rgba(255,255,255,0.07); }
        .modal-hdr h2 { margin:0; font-size:18px; color:#F9FAFB; }
        .modal-x { background:none; border:none; font-size:28px; cursor:pointer; color:#6B7280; line-height:1; transition:color 0.15s; }
        .modal-x:hover { color:#F87171; }
        .modal-bdy { padding:24px; display:flex; flex-direction:column; gap:16px; max-height:60vh; overflow-y:auto; }
        .modal-ftr { display:flex; gap:10px; justify-content:flex-end; padding:16px 24px; border-top:1px solid rgba(255,255,255,0.07); }

        .fgrp { display:flex; flex-direction:column; gap:6px; flex:1; }
        .fgrp label { font-size:11px; font-weight:700; color:#6B7280; text-transform:uppercase; letter-spacing:0.05em; }
        .finput, .fselect { padding:11px 14px; background:rgba(255,255,255,0.05); border:1px solid rgba(255,255,255,0.08); border-radius:10px; color:#E5E7EB; font-size:14px; outline:none; transition:border-color 0.2s; width:100%; box-sizing:border-box; }
        .finput:focus, .fselect:focus { border-color:rgba(167,139,250,0.5); background:rgba(255,255,255,0.07); }
        .fselect option { background:#13131a; }
        .frow { display:flex; gap:12px; }
        .form-error { background:rgba(248,113,113,0.1); border:1px solid rgba(248,113,113,0.25); color:#FCA5A5; padding:10px 14px; border-radius:9px; font-size:13px; }

        .elevenlabs-section { background:rgba(167,139,250,0.05); border:1px solid rgba(167,139,250,0.15); border-radius:12px; padding:16px; display:flex; flex-direction:column; gap:12px; }
        .section-label { font-size:12px; font-weight:700; color:#a78bfa; text-transform:uppercase; letter-spacing:0.05em; }

        .tools-section { border-top:1px solid rgba(167,139,250,0.15); padding-top:12px; display:flex; flex-direction:column; gap:10px; }
        .tools-hint { font-size:12px; color:#9CA3AF; line-height:1.6; margin:0; }
        .tools-hint code { background:rgba(255,255,255,0.08); padding:1px 5px; border-radius:4px; }
        .tool-card { background:rgba(255,255,255,0.03); border:1px solid rgba(255,255,255,0.08); border-radius:10px; padding:10px 12px; }
        .tool-card-hdr { display:flex; justify-content:space-between; align-items:center; margin-bottom:4px; }
        .tool-name { font-family:monospace; font-size:13px; font-weight:700; color:#a78bfa; }
        .tool-copy-btn { padding:4px 10px; background:rgba(167,139,250,0.15); border:1px solid rgba(167,139,250,0.25); border-radius:7px; color:#c4b5fd; font-size:11px; cursor:pointer; }
        .tool-copy-btn:hover { background:rgba(167,139,250,0.25); }
        .tool-desc { font-size:12px; color:#9CA3AF; margin-bottom:6px; }
        .tool-url { font-family:monospace; font-size:11px; color:#6EE7B7; word-break:break-all; background:rgba(0,0,0,0.2); padding:6px 8px; border-radius:6px; margin-bottom:6px; }
        .tool-params { font-family:monospace; font-size:11px; color:#D1D5DB; background:rgba(0,0,0,0.2); padding:8px; border-radius:6px; margin:0; overflow-x:auto; }

        .btn-cancel { padding:10px 20px; background:rgba(255,255,255,0.06); border:1px solid rgba(255,255,255,0.08); border-radius:10px; color:#9CA3AF; font-size:14px; cursor:pointer; transition:all 0.2s; }
        .btn-cancel:hover { background:rgba(255,255,255,0.1); }
        .btn-save { padding:10px 24px; background:linear-gradient(135deg,#7c3aed,#a78bfa); border:none; border-radius:10px; color:#fff; font-size:14px; font-weight:700; cursor:pointer; transition:opacity 0.2s; }
        .btn-save:disabled { opacity:0.6; cursor:not-allowed; }
      `}</style>
    </div>
  );
};

export default AgentsTab;
